/**
 * AdminPortal Web Component
 *
 * Admin Portal table: paginated user list with an owner-only role dropdown.
 * All security checks are server-side; this component only hides controls
 * the current role can't use.
 *
 * Usage:
 *   <admin-portal></admin-portal>
 *   portal.setRole('owner' | 'admin')
 *   portal.setUsers(users, hasMore)
 *   portal.appendUsers(users, hasMore)
 *   portal.setLoading(true|false)
 *   portal.setError('message')
 *
 * Events:
 *   - role-change: { detail: { targetUid, toRole } }
 *   - page-request: fired when "Load more" is clicked
 *
 * Spec §XI.3 (UI), AC-3, AC-4, AC-15
 */

import { escapeHtml } from '../utils/escape-html.js';
import './AdminPortal.css';

const ROLE_LABEL = Object.freeze({
    owner: 'Owner',
    admin: 'Admin',
    user: 'User',
});

function formatDate(value) {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export class AdminPortal extends HTMLElement {
    constructor() {
        super();
        this.users = [];
        this.role = null; // 'owner' | 'admin' | null
        this.loading = false;
        this.error = null;
        this.hasMore = false;
        this.loadingMore = false;
        this.query = '';
    }

    connectedCallback() {
        this.render();
        this.attachDelegatedListeners();
    }

    setRole(role) {
        this.role = role;
        this.render();
    }

    setUsers(users, hasMore = false) {
        this.users = users ?? [];
        this.hasMore = !!hasMore;
        this.error = null;
        this.loading = false;
        this.loadingMore = false;
        this.render();
    }

    appendUsers(users, hasMore = false) {
        this.users = this.users.concat(users ?? []);
        this.hasMore = !!hasMore;
        this.loadingMore = false;
        this.render();
    }

    setLoading(loading) {
        this.loading = loading;
        if (loading) this.error = null;
        this.render();
    }

    setLoadingMore(loadingMore) {
        this.loadingMore = loadingMore;
        this.render();
    }

    setError(message) {
        this.error = message;
        this.loading = false;
        this.loadingMore = false;
        this.render();
    }

    /**
     * Update the dropdown on a specific row after a successful setUserRole
     * call, so the table reflects the change without a refetch.
     *
     * Note there is no snapshot listener on the admin list — it is a one-shot
     * paginated read — so this local update is the only thing keeping the row
     * current until the page is reloaded.
     */
    updateUserRole(uid, newRole) {
        const u = this.users.find((x) => x.uid === uid);
        if (u) u.role = newRole;
        this.render();
    }

    render() {
        this.innerHTML = this.renderShell();
    }

    renderShell() {
        return `
<section class="admin-portal">
  <header class="admin-portal__header">
    <h1>Admin Portal</h1>
    <p class="admin-portal__subtitle">
      ${
          this.role === 'owner'
              ? 'Manage users and change roles'
              : 'View users (role changes require owner privileges)'
      }
    </p>
  </header>
  ${this.renderSearchBar()}
  <div class="admin-portal__results">${this.renderInner()}</div>
</section>`;
    }

    renderInner() {
        if (this.loading) return this.renderLoading();
        if (this.error) return this.renderError();
        if (this.users.length === 0) return this.renderEmpty();
        return this.renderTable();
    }

    renderSearchBar() {
        if (this.loading || this.error || this.users.length === 0) return '';
        return `
<div class="admin-portal__search-bar">
  <input type="search" class="admin-portal__search"
    placeholder="Filter loaded users"
    aria-label="Filter the loaded users by name or email"
    value="${escapeHtml(this.query)}" />
  ${
      this.hasMore
          ? '<p class="admin-portal__search-hint">Filters the users loaded so far. Use “Load more” to include the rest.</p>'
          : ''
  }
</div>`;
    }

    filteredUsers() {
        const q = this.query.trim().toLowerCase();
        if (!q) return this.users;
        return this.users.filter((u) => {
            const name = (u.displayName || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    }

    renderLoading() {
        return `<div class="admin-portal__status"><loading-spinner></loading-spinner></div>`;
    }

    renderError() {
        return `
<div class="admin-portal__status admin-portal__status--error">
  <p>${escapeHtml(this.error)}</p>
  <button type="button" data-action="retry">Retry</button>
</div>`;
    }

    renderEmpty() {
        return `<div class="admin-portal__status"><p>No users to display.</p></div>`;
    }

    renderTable() {
        const filtered = this.filteredUsers();
        const footer = this.hasMore
            ? `<button type="button" class="admin-portal__load-more"
                 data-action="load-more" ${this.loadingMore ? 'disabled' : ''}>
                 ${this.loadingMore ? 'Loading…' : 'Load more'}
               </button>`
            : '';
        if (filtered.length === 0) {
            // Filtering is client-side over the loaded page only, so "no
            // matches" is not the same as "no such user". Saying so prevents
            // the previous behaviour, where a user on page 2+ looked absent.
            const more = this.hasMore
                ? ' More users have not been loaded yet — use “Load more” and filter again.'
                : '';
            return `
<div class="admin-portal__status">
  <p>No loaded users match "${escapeHtml(this.query.trim())}".${more}</p>
</div>
${footer}`;
        }
        const rows = filtered.map((u) => this.renderRow(u)).join('');
        return `
<div class="admin-portal__table-wrap">
  <table class="admin-portal__table">
    <thead>
      <tr>
        <th scope="col">User</th>
        <th scope="col">Email</th>
        <th scope="col">Joined</th>
        <th scope="col">Role</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</div>
${footer}`;
    }

    renderRow(u) {
        const displayName = escapeHtml(u.displayName || '(no name)');
        const email = escapeHtml(u.email || '—');
        const photo = escapeHtml(u.photoURL || '');
        return `
<tr data-uid="${escapeHtml(u.uid)}">
  <td class="admin-portal__user-cell">
    <user-avatar photo="${photo}" alt="${displayName}'s avatar" size="small"></user-avatar>
    <span class="admin-portal__display-name">${displayName}</span>
  </td>
  <td data-label="Email">${email}</td>
  <td data-label="Joined">${formatDate(u.createdAt)}</td>
  <td data-label="Role">${this.renderRoleCell(u)}</td>
</tr>`;
    }

    renderRoleCell(u) {
        if (this.role !== 'owner') {
            return `<span class="admin-portal__role-badge admin-portal__role-badge--${escapeHtml(u.role)}">
                      ${ROLE_LABEL[u.role] ?? escapeHtml(u.role)}
                    </span>`;
        }
        // Owner: render a select so the owner can change the role
        const options = ['owner', 'admin', 'user']
            .map(
                (r) =>
                    `<option value="${r}" ${r === u.role ? 'selected' : ''}>${ROLE_LABEL[r]}</option>`,
            )
            .join('');
        return `
<select class="admin-portal__role-select"
        data-action="role-change"
        data-uid="${escapeHtml(u.uid)}"
        aria-label="Change role for ${escapeHtml(u.displayName || u.uid)}">
  ${options}
</select>`;
    }

    attachDelegatedListeners() {
        this.addEventListener('click', (e) => {
            const target = /** @type {HTMLElement|null} */ (e.target);
            const action = target?.dataset?.action;
            if (action === 'load-more') {
                this.setLoadingMore(true);
                this.dispatchEvent(new CustomEvent('page-request', { bubbles: true }));
            }
            if (action === 'retry') {
                this.dispatchEvent(new CustomEvent('retry-request', { bubbles: true }));
            }
        });

        this.addEventListener('change', (e) => {
            const target = /** @type {HTMLSelectElement|null} */ (e.target);
            if (target?.dataset?.action !== 'role-change') return;
            const targetUid = target.dataset.uid;
            const toRole = target.value;
            this.dispatchEvent(
                new CustomEvent('role-change', {
                    bubbles: true,
                    detail: { targetUid, toRole },
                }),
            );
        });

        this.addEventListener('input', (e) => {
            const target = /** @type {HTMLInputElement|null} */ (e.target);
            if (!target?.classList?.contains('admin-portal__search')) return;
            this.query = target.value;
            const container = this.querySelector('.admin-portal__results');
            if (container) container.innerHTML = this.renderInner();
        });
    }
}

if (!customElements.get('admin-portal')) {
    customElements.define('admin-portal', AdminPortal);
}
